import React, { useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import styled from "styled-components";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PaidIcon from "@mui/icons-material/Paid";
import {
  ServicioTransporte,
  useBuscarServiciosCompatibles,
} from "../../../../services/transporteServicios";
import { HeaderSection } from "../HeaderSection";
import { Title } from "../Title";
import ServerErrorModal from "../../../organisms/ServerErrorModal";
import { Model } from "../Model";
import Resumen from "../Resumen/Resumen";
import SuccessButton from "../../../organisms/SuccessButton";
import DatosTransporte from "../DatosTransporte/DatosTransporte";
import { usePasoReservaGuarderia } from "../pasoReservaGuarderia";
import { fechasSonIguales } from "../../../../utils/dateUtils";
import FechasTransporte from "../FechasTransporte/FechasTransporte";
import SeccionTarifas from "../../../calculoPrecioTransporte/SeccionTarifas";

function CalculoPrecioTransporte({ model }: { model: Model }) {
  const {
    currentModel,
    definirTarifaIda,
    definirTarifaVuelta,
    definirTarifaIdaVuelta,
    reiniciarTarifasTransporte,
    definirUsarTarifasPorTrayecto,
  } = usePasoReservaGuarderia(model);
  const [avanzar, setAvanzar] = useState(false);
  const [volver, setVolver] = useState(false);

  const {
    data: planesCompatibles,
    isLoading: planesCompatiblesIsLoading,
    error: planesCompatiblesError,
  } = useBuscarServiciosCompatibles(model.sectorTransporte!!.id);

  const onSiguienteClick = () => {
    setAvanzar(true);
  };

  const onVolverClick = () => {
    setVolver(true);
  };

  const onSeleccionarTarifaIda = (tarifa: ServicioTransporte) => {
    definirTarifaIda({ tarifaIda: tarifa });
  };

  const onSeleccionarTarifaVuelta = (tarifa: ServicioTransporte) => {
    definirTarifaVuelta({ tarifaVuelta: tarifa });
  };

  const onSeleccionarTarifaIdaYVuelta = (tarifa: ServicioTransporte) => {
    definirTarifaIdaVuelta({ tarifaIdaVuelta: tarifa });
  };

  const onUsarTarifasPorCadaTrayectoToggle = () => {
    definirUsarTarifasPorTrayecto({
      usarTarifasPorTrayecto: !currentModel.usarTarifasPorTrayecto,
    });
    reiniciarTarifasTransporte();
  };

  const puedeAvanzar = calcularPuedeAvanzar(currentModel);

  const error = planesCompatiblesError;

  useEffect(() => {
    if (
      planesCompatibles &&
      !currentModel.tarifaIda &&
      !currentModel.tarifaVuelta &&
      !currentModel.tarifaIdaVuelta
    ) {
      if (mostrarTarifasIda(currentModel)) {
        const tarifasIda = planesCompatibles.unTrayecto;
        if (tarifasIda.length === 1) {
          definirTarifaIda({ tarifaIda: tarifasIda[0] });
        }
      }
      if (mostrarTarifasVuelta(currentModel)) {
        const tarifasVuelta = planesCompatibles.unTrayecto;
        if (tarifasVuelta.length === 1) {
          definirTarifaVuelta({ tarifaVuelta: tarifasVuelta[0] });
        }
      }
      if (mostrarTarifasIdaVuelta(currentModel)) {
        const tarifasIdaVuelta = planesCompatibles.dosTrayectos;
        if (tarifasIdaVuelta.length === 1) {
          definirTarifaIdaVuelta({ tarifaIdaVuelta: tarifasIdaVuelta[0] });
        }
      }
    }
  }, [currentModel, planesCompatibles, definirTarifaIda, definirTarifaVuelta, definirTarifaIdaVuelta]);

  if (avanzar) {
    return <Resumen model={currentModel} />;
  }

  if (volver) {
    if (fechasSonIguales(model.fechasProgramadas, model.fechasTransporte)) {
      return <DatosTransporte model={currentModel} />;
    } else {
      return <FechasTransporte model={currentModel} />;
    }
  }

  return (
    <>
      {error && <ServerErrorModal />}
      <Row className="justify-content-center">
        <Col className="col-10">
          <HeaderSection className="pt-5 pb-4">
            <Title>
              Cálculo de precios de transporte{""}
              <IconContainer>
                <PaidIcon sx={{ fontSize: 26 }} />
              </IconContainer>
            </Title>
          </HeaderSection>
          <Row>
            <div>
              <b>Sector: </b> {model.sectorTransporte!!.nombre}
            </div>
            {model.contrataIda && model.contrataVuelta && (
              <div className="mb-4 mt-3">
                <label className="mb-1">Usar tarifas por cada trayecto</label>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="flexSwitchCheckDefault"
                    checked={currentModel.usarTarifasPorTrayecto}
                    onChange={onUsarTarifasPorCadaTrayectoToggle}
                  />
                  <label>
                    {currentModel.usarTarifasPorTrayecto ? "Si" : "No"}{" "}
                  </label>
                </div>
              </div>
            )}
            {mostrarTarifasIda(currentModel) && (
              <SeccionTarifas
                tipoTrayecto="ida"
                isLoading={planesCompatiblesIsLoading}
                tarifas={planesCompatibles?.unTrayecto || []}
                onSeleccionarTarifa={onSeleccionarTarifaIda}
                tarifaSeleccionada={currentModel.tarifaIda}
              />
            )}
            {mostrarTarifasVuelta(currentModel) && (
              <SeccionTarifas
                tipoTrayecto="vuelta"
                isLoading={planesCompatiblesIsLoading}
                tarifas={planesCompatibles?.unTrayecto || []}
                onSeleccionarTarifa={onSeleccionarTarifaVuelta}
                tarifaSeleccionada={currentModel.tarifaVuelta}
              />
            )}
            {mostrarTarifasIdaVuelta(currentModel) && (
              <SeccionTarifas
                tipoTrayecto="ida_y_vuelta"
                isLoading={planesCompatiblesIsLoading}
                tarifas={planesCompatibles?.dosTrayectos || []}
                onSeleccionarTarifa={onSeleccionarTarifaIdaYVuelta}
                tarifaSeleccionada={currentModel.tarifaIdaVuelta}
              />
            )}

            <div className="d-flex mb-2 justify-content-between">
              <Button
                type={"button"}
                style={{ width: "fit-content" }}
                onClick={onVolverClick}
              >
                <ArrowBackIcon sx={{ fontSize: 20 }} />
                Volver
              </Button>
              <SuccessButton
                type={"button"}
                style={{ width: "fit-content" }}
                disabled={!puedeAvanzar}
                onClick={onSiguienteClick}
              >
                Siguiente <ArrowForwardIcon sx={{ fontSize: 20 }} />
              </SuccessButton>
            </div>
          </Row>
        </Col>
      </Row>
    </>
  );
}

const IconContainer = styled("span")`
  display: relative;
  color: ${(props) => props.theme.colors.main};
  margin-left: 4px;
`;

const calcularPuedeAvanzar = (model: Model): boolean => {
  if (!!model.tarifaIdaVuelta) {
    return true;
  }

  if (model.contrataIda && !model.tarifaIda) {
    return false;
  }

  if (model.contrataVuelta && !model.tarifaVuelta) {
    return false;
  }

  return true;
};

const mostrarTarifasIda = (model: Model) => {
  return (
    (model.contrataIda &&
      model.contrataVuelta &&
      model.usarTarifasPorTrayecto) ||
    (model.contrataIda && !model.contrataVuelta)
  );
};

const mostrarTarifasVuelta = (model: Model) => {
  return (
    (model.contrataVuelta &&
      model.contrataIda &&
      model.usarTarifasPorTrayecto) ||
    (model.contrataVuelta && !model.contrataIda)
  );
};

const mostrarTarifasIdaVuelta = (model: Model) => {
  return (
    model.contrataIda && model.contrataVuelta && !model.usarTarifasPorTrayecto
  );
};

export default CalculoPrecioTransporte;
